import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ServiceHistory = ({ services, onRebook, onViewPhotos, onDownloadInvoice }) => {
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10 border-success/20';
      case 'cancelled':
        return 'text-error bg-error/10 border-error/20';
      case 'no-show':
        return 'text-warning bg-warning/10 border-warning/20';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const filteredServices = services?.filter(service => {
    if (filter === 'all') return true;
    return service?.status === filter;
  });

  const totalPages = Math.ceil(filteredServices?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedServices = filteredServices?.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            Service History
          </h2>
          <span className="text-sm text-text-secondary">
            {filteredServices?.length} services
          </span>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1">
          {[
            { key: 'all', label: 'All Services' },
            { key: 'completed', label: 'Completed' },
            { key: 'cancelled', label: 'Cancelled' }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => {
                setFilter(tab?.key);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                filter === tab?.key
                  ? 'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-muted'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {paginatedServices?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="History" size={32} color="var(--color-text-secondary)" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">
              No service history
            </h3>
            <p className="text-text-secondary">
              Your completed services will appear here
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Service</th>
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Technician</th>
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedServices?.map((service) => (
                    <tr key={service?.id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-text-primary">{service?.name}</div>
                          <div className="text-sm text-text-secondary">{service?.location}</div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-text-secondary">
                        {formatDate(service?.date)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden">
                            <Image
                              src={service?.technician?.avatar}
                              alt={service?.technician?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-text-primary">{service?.technician?.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(service?.status)}`}>
                          {service?.status?.charAt(0)?.toUpperCase() + service?.status?.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4 font-semibold text-text-primary">
                        ${service?.amount}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          {service?.status === 'completed' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="RotateCcw"
                                onClick={() => onRebook(service?.id)}
                              />
                              {service?.photos && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Image"
                                  onClick={() => onViewPhotos(service?.id)}
                                />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Download"
                                onClick={() => onDownloadInvoice(service?.id)}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4">
              {paginatedServices?.map((service) => (
                <div key={service?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-text-primary">{service?.name}</h3>
                      <p className="text-sm text-text-secondary">{service?.location}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(service?.status)}`}>
                      {service?.status?.charAt(0)?.toUpperCase() + service?.status?.slice(1)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full overflow-hidden">
                        <Image
                          src={service?.technician?.avatar}
                          alt={service?.technician?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-sm text-text-primary">{service?.technician?.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-text-primary">${service?.amount}</div>
                      <div className="text-sm text-text-secondary">{formatDate(service?.date)}</div>
                    </div>
                  </div>

                  {service?.status === 'completed' && (
                    <div className="flex items-center space-x-2 pt-3 border-t border-border">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCcw"
                        iconPosition="left"
                        onClick={() => onRebook(service?.id)}
                      >
                        Rebook
                      </Button>
                      {service?.photos && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Image"
                          onClick={() => onViewPhotos(service?.id)}
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        onClick={() => onDownloadInvoice(service?.id)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
                <div className="text-sm text-text-secondary">
                  Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredServices?.length)} of {filteredServices?.length}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronLeft"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  />
                  <span className="text-sm text-text-primary">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronRight"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceHistory;